import React, { useState } from "react";
import {
  TextField,
  Autocomplete,
  Box,
  FormControl,
  FormHelperText,
  IconButton,
} from "@mui/material";
import { NavLink } from "react-router-dom";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { dummyFirms } from "../../../dummyData";
import { FileText, RefreshCw } from "lucide-react";
import BreadCrumbsContainer from "../../../Components/Common/Breadcrumbs";
import QuickDateRange from "./Components/QuickDateRange";
import dayjs from "dayjs";

const Ledger = () => {
  const [selectedFirm, setSelectedFirm] = useState(null);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [errors, setErrors] = useState({});
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [timeRange, setTimeRange] = useState("lastMonth");

  const validateForm = () => {
    let isValid = true;
    const newErrors = {};

    if (!selectedFirm) {
      newErrors.firm = "Please select a firm";
      isValid = false;
    }

    if (!fromDate) {
      newErrors.fromDate = "Please select from date";
      isValid = false;
    }

    if (!toDate) {
      newErrors.toDate = "Please select to date";
      isValid = false;
    }

    if (fromDate && toDate && fromDate > toDate) {
      newErrors.dateRange = "From date cannot be after to date";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = e => {
    e.preventDefault();
    if (validateForm() && selectedFirm) {
      // Navigate programmatically after validation
      // For now, we'll keep the NavLink approach
    }
  };

  const handleRefresh = () => {
    setIsRefreshing(true);
    setSelectedFirm(null);
    setFromDate(null);
    setToDate(null);
    setTimeRange("lastMonth");
    setErrors({});
    setTimeout(() => setIsRefreshing(false), 500);
  };

  const handleRangeChange = (range, fromDateValue, toDateValue) => {
    setTimeRange(range);

    if (fromDateValue && toDateValue) {
      // Convert from dayjs to Date object for compatibility with DateFns adapter
      setFromDate(fromDateValue.toDate());
      setToDate(toDateValue.toDate());

      // Clear any date-related errors when date range changes
      setErrors({
        ...errors,
        fromDate: undefined,
        toDate: undefined,
        dateRange: undefined,
      });
    }
  };

  return (
    <div>
      <div className="bg-white h-[calc(100vh-120px)] rounded-lg overflow-auto">
        {/* Header Section */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h1 className="text-2xl font-semibold font-outfit text-gray-800">
            Ledger Report
          </h1>
        </div>

        <div className="p-6">
          <div className="mb-6 flex items-center gap-2 text-gray-700">
            <FileText size={20} />
            <span className="font-medium">
              Please fill in the details to generate a ledger report
            </span>
          </div>

          <form onSubmit={handleSubmit} className="max-w-4xl">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 w-full">
              <FormControl error={!!errors.firm} fullWidth>
                <Autocomplete
                  id="firm-autocomplete"
                  options={dummyFirms}
                  getOptionLabel={option => option.firmName}
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      <div className="flex flex-col">
                        <span className="font-medium">{option.firmName}</span>
                        <span className="text-xs text-gray-500">
                          {option.ownerName} | {option.phoneNumber}
                        </span>
                      </div>
                    </Box>
                  )}
                  renderInput={params => (
                    <TextField
                      {...params}
                      label="Firm Name"
                      variant="outlined"
                      size="small"
                      required
                      error={!!errors.firm}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "&.Mui-focused fieldset": {
                            borderColor: "#5652B0",
                          },
                        },
                        "& .MuiInputLabel-root.Mui-focused": {
                          color: "#5652B0",
                        },
                      }}
                    />
                  )}
                  value={selectedFirm}
                  onChange={(_, newValue) => {
                    setSelectedFirm(newValue);
                    if (newValue) {
                      setErrors({ ...errors, firm: undefined });
                    }
                  }}
                />
                {errors.firm && <FormHelperText>{errors.firm}</FormHelperText>}
              </FormControl>

              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <FormControl error={!!errors.fromDate} fullWidth>
                  <DatePicker
                    label="From Date"
                    value={fromDate}
                    onChange={newValue => {
                      setFromDate(newValue);
                      if (newValue) {
                        setErrors({
                          ...errors,
                          fromDate: undefined,
                          dateRange: undefined,
                        });
                      }
                      setTimeRange("custom"); // Switch to custom when date is manually changed
                    }}
                    slotProps={{
                      textField: {
                        size: "small",
                        required: true,
                        error: !!errors.fromDate || !!errors.dateRange,
                        helperText:
                          errors.fromDate ||
                          (errors.dateRange && errors.fromDate
                            ? errors.dateRange
                            : ""),
                        sx: {
                          "& .MuiOutlinedInput-root": {
                            "&.Mui-focused fieldset": {
                              borderColor: "#5652B0",
                            },
                          },
                          "& .MuiInputLabel-root.Mui-focused": {
                            color: "#5652B0",
                          },
                        },
                      },
                    }}
                  />
                </FormControl>
              </LocalizationProvider>

              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <FormControl error={!!errors.toDate} fullWidth>
                  <DatePicker
                    label="To Date"
                    value={toDate}
                    onChange={newValue => {
                      setToDate(newValue);
                      if (newValue) {
                        setErrors({
                          ...errors,
                          toDate: undefined,
                          dateRange: undefined,
                        });
                      }
                      setTimeRange("custom"); // Switch to custom when date is manually changed
                    }}
                    slotProps={{
                      textField: {
                        size: "small",
                        required: true,
                        error: !!errors.toDate || !!errors.dateRange,
                        helperText:
                          errors.toDate ||
                          (errors.dateRange && !errors.fromDate
                            ? errors.dateRange
                            : ""),
                        sx: {
                          "& .MuiOutlinedInput-root": {
                            "&.Mui-focused fieldset": {
                              borderColor: "#5652B0",
                            },
                          },
                          "& .MuiInputLabel-root.Mui-focused": {
                            color: "#5652B0",
                          },
                        },
                      },
                    }}
                  />
                </FormControl>
              </LocalizationProvider>
            </div>

            <QuickDateRange
              selectedRange={timeRange}
              onRangeChange={handleRangeChange}
              showFinancialYears={true}
            />

            <div className="mt-8 flex gap-4">
              {selectedFirm ? (
                <NavLink to={`/report/ledger/${selectedFirm.id}`}>
                  <button
                    type="submit"
                    className="px-6 py-2.5 rounded-lg text-base font-medium text-white bg-[#5652B0] hover:bg-[#4A47A3] transition-all">
                    Generate Ledger
                  </button>
                </NavLink>
              ) : (
                <button
                  type="submit"
                  className="px-6 py-2.5 rounded-lg text-base font-medium text-white bg-[#5652B0] hover:bg-[#4A47A3] transition-all">
                  Generate Ledger
                </button>
              )}

              <button
                type="button"
                onClick={handleRefresh}
                className="px-6 py-2.5 rounded-lg text-base font-medium border-2 border-[#5652B0] text-[#5652B0] hover:bg-[#5652B0] hover:text-white transition-all flex items-center gap-2">
                <RefreshCw
                  size={16}
                  className={isRefreshing ? "animate-spin" : ""}
                />
                Reset
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Ledger;
